/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MarkdownRenderer } from '@/components/ui/markdown-renderer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Copy, Check, Download, Clock, FileCode } from 'lucide-react';
import { useState } from 'react';

interface AgentsMdViewerProps {
  /** The AGENTS.md content to display */
  content: string;
  /** Title to show in the header */
  title?: string;
  /** Optional subtitle or description */
  subtitle?: string;
  /** File path for display */
  path?: string;
  /** Last modified timestamp */
  lastModified?: string;
  /** Show action buttons (copy, download) */
  showActions?: boolean;
  /** Maximum height of the viewer */
  maxHeight?: string;
  /** Additional header actions */
  headerActions?: React.ReactNode;
  /** Whether to show the card wrapper */
  showCard?: boolean;
}

export function AgentsMdViewer({
  content,
  title = 'AGENTS.md',
  subtitle,
  path,
  lastModified,
  showActions = true,
  maxHeight = '600px',
  headerActions,
  showCard = true,
}: AgentsMdViewerProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'AGENTS.md';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const viewerContent = (
    <>
      {/* Metadata Row */}
      {(path || lastModified) && (
        <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground border-b pb-3">
          {path && (
            <div className="flex items-center gap-1">
              <FileCode className="h-4 w-4" />
              <span className="font-mono">{path}</span>
            </div>
          )}
          {lastModified && (
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>Modified: {formatDate(lastModified)}</span>
            </div>
          )}
        </div>
      )}

      {/* Markdown Content */}
      <ScrollArea style={{ maxHeight }}>
        <MarkdownRenderer content={content} />
      </ScrollArea>
    </>
  );

  if (!showCard) {
    return viewerContent;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileCode className="h-5 w-5 text-primary" />
            {title}
          </CardTitle>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {showActions && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
              >
                {copied ? (
                  <Check className="h-4 w-4 mr-1 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4 mr-1" />
                )}
                {copied ? 'Copied' : 'Copy'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
              >
                <Download className="h-4 w-4 mr-1" />
                Download
              </Button>
            </>
          )}
          {headerActions}
        </div>
      </CardHeader>
      <CardContent>
        {viewerContent}
      </CardContent>
    </Card>
  );
}
